import { Logout as LogoutIcon } from '@mui/icons-material';
import { useMediaQuery, useTheme } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { resolveNetworkRedirect, routes, useNetworkState } from '#shared';

import { useToast } from '../../LayoutManager';
import { useSsoUserGroupAccess } from '../../SsoAccess';
import {
  DELIM,
  cleanDelimiters,
  commandCenterMenuItems,
  hasCC,
  hasStats,
  statsMenuItems,
} from '../lib';

import { MenuItem } from './types';

const NEUTRAL_ROUTES: string[] = [
  routes.LOGOUT,
  routes.NO_ACCESS,
  routes.PAGE_NOT_FOUND,
];
const isStatsRoute = (path: string): boolean => path.startsWith('/stats');
const isNeutralRoute = (path: string): boolean => NEUTRAL_ROUTES.includes(path);

const messageForBlock = (block: string): string => {
  if (block === routes.ERRORS) {
    return 'No network is reachable right now — cannot open that page.';
  }
  if (block === routes.NO_ACCESS) {
    return 'You don’t have access to a page that’s reachable right now.';
  }
  if (block.startsWith('/stats')) {
    return 'This page requires the VPN, which is currently unreachable.';
  }
  return 'This page requires the public Statistics service, which is currently unreachable.';
};

export const useNavigationMenu = () => {
  const { status, grantedGroups } = useSsoUserGroupAccess();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('md'));
  const { recheck } = useNetworkState();
  const { showToast, ToastComponent } = useToast();

  const handleSwitch = useCallback(
    async (url: string) => {
      // Only re-check connectivity when crossing the main-app ↔ stats-site
      // boundary. Same-side navigation (main→main, stats→stats) and neutral
      // routes (logout/403/404) trust the existing state — kiosks shuffling
      // between widgets on the same side don't pay the health-check latency
      // and the stays-put-on-VPN-blip invariant is preserved.
      const crossesBoundary =
        !isNeutralRoute(url) && isStatsRoute(pathname) !== isStatsRoute(url);
      if (crossesBoundary) {
        const fresh = await recheck();
        const block = resolveNetworkRedirect(
          undefined,
          url,
          fresh,
          grantedGroups,
        );
        if (block) {
          showToast(messageForBlock(block), 'warning');
          return;
        }
      }
      navigate(url);
    },
    [grantedGroups, navigate, pathname, recheck, showToast],
  );

  const menu = useMemo(() => {
    if (status === 'pending') {
      return [];
    }

    const cc = hasCC(grantedGroups);
    const stats = hasStats(grantedGroups);

    // canonical order (desktop order)
    const all: MenuItem[] = [
      DELIM,
      ...(cc ? [...commandCenterMenuItems, DELIM] : []),
      ...(stats ? [...statsMenuItems, DELIM] : []),
      {
        id: routes.LOGOUT,
        icon: <LogoutIcon />,
        type: 'route',
        title: 'Logout',
        showOnSmall: 'show',
      },
    ];

    if (!isSmall) {
      return cleanDelimiters(all);
    }

    const filtered = all.filter(it => {
      if (it.type === 'delimiter') {
        return true;
      }
      const rule = it.showOnSmall ?? 'hide';
      if (rule === 'show') {
        return true;
      }
      if (rule === 'cc') {
        return cc;
      }
      if (rule === 'stats') {
        return stats;
      }
      return false;
    });

    return cleanDelimiters(filtered);
  }, [grantedGroups, isSmall, status]);

  return { menu, handleSwitch, ToastComponent };
};
